import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import LuggageScan from "@/components/luggage/LuggageScan";
import LuggageLockers from "@/components/luggage/LuggageLockers";
import LuggageHistory from "@/components/luggage/LuggageHistory";
import { QrCode, Grid3X3, History, Luggage as LuggageIcon } from "lucide-react";

const MANAGER_ROLES = ["managing_director", "executive_director", "general_manager"];

function StatCard({ label, value, tone = "slate", testid }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    red: "bg-red-50 text-red-800 border-red-100",
    slate: "bg-white text-gray-800 border-gray-200",
  };
  return (
    <div className={`rounded-xl border p-3 text-center ${tones[tone]}`} data-testid={testid}>
      <div className="text-xl font-bold font-display">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.12em] opacity-70 mt-0.5">{label}</div>
    </div>
  );
}

export default function Luggage() {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [stats, setStats] = useState({});
  const [branches, setBranches] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    api.get("/luggage/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="luggage-page">
      <PageHeader
        eyebrow="Operations"
        title="Luggage Area"
        subtitle={
          stats.branch
            ? `Smart luggage desk for ${stats.branch} — scan a card to deposit or release luggage.`
            : "Smart luggage desk — scan entry cards to deposit or release customer luggage. Managers generate & print branch card sets."
        }
      />

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 mb-6">
        <StatCard label="Cards" value={stats.total ?? 0} testid="lug-stat-total" />
        <StatCard label="Free" value={stats.free ?? 0} tone="emerald" testid="lug-stat-free" />
        <StatCard label="In use" value={stats.occupied ?? 0} tone="amber" testid="lug-stat-occupied" />
        <StatCard label={`Overdue >${stats.overdue_hours ?? 8}h`} value={stats.overdue ?? 0}
          tone={stats.overdue > 0 ? "red" : "slate"} testid="lug-stat-overdue" />
        <StatCard label="Lost" value={stats.lost ?? 0} tone={stats.lost > 0 ? "red" : "slate"} testid="lug-stat-lost" />
        <StatCard label="Today in" value={stats.today_checkins ?? 0} testid="lug-stat-today-in" />
        <StatCard label="Today out" value={stats.today_checkouts ?? 0} testid="lug-stat-today-out" />
      </div>

      <Tabs defaultValue="scan">
        <TabsList className="mb-4">
          <TabsTrigger value="scan" data-testid="lug-tab-scan">
            <QrCode size={14} className="mr-1.5" /> Scan & Serve
          </TabsTrigger>
          <TabsTrigger value="lockers" data-testid="lug-tab-lockers">
            <Grid3X3 size={14} className="mr-1.5" /> Lockers & Cards
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="lug-tab-history">
            <History size={14} className="mr-1.5" /> History
          </TabsTrigger>
        </TabsList>
        <TabsContent value="scan">
          <LuggageScan onChanged={refresh} />
        </TabsContent>
        <TabsContent value="lockers">
          <LuggageLockers isManager={isManager} branches={branches}
            refreshKey={refreshKey} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="history">
          <LuggageHistory branches={branches} isManager={isManager} refreshKey={refreshKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
